import { useQuery, } from 'react-query';

import { type IUser } from 'src/api/users-api';
import { queryKeys } from 'src/services/api/constants';
import { apiClient } from 'src/services/api';

type GetTeamMembersResponse = {
    members: IUser[];
};

type IGetTeamMembersOptions = {
    includeInactive?: boolean;
    refetchInterval?: number | false;
};

/**
 * Fetches team members. By default, returns only active members.
 * Pass includeInactive: true to include deactivated members (only for Team Members management page).
 *
 * The endpoint is guaranteed to return every member of the caller's leasing team,
 * including the current user — so counts like "other teammates" can safely subtract
 * one for self.
 *
 * `includeInactive` is a purely client-side filter — the endpoint always returns
 * the full roster and we narrow it in `select`. It is therefore deliberately NOT
 * part of the query key: every caller (active-only or not) shares a single cached
 * fetch, and toggling `includeInactive` re-derives the list from cache without an
 * extra request.
 */
export const useTeamMembers = (options?: IGetTeamMembersOptions) => {
    const includeInactive = options?.includeInactive ?? false;
    const refetchInterval = options?.refetchInterval ?? false;
    return useQuery(
        queryKeys.teamMembers,
        async () => {
            const res = await apiClient('api/landlord/team/members').json<GetTeamMembersResponse>();
            return res.members;
        },
        {
            select: (members) =>
                includeInactive ? members : members.filter(member => member.is_active),
            refetchInterval,
        },
    );
};

export default useTeamMembers;